import { Button, Card, CardBody, CardHeader, Input, Paragraph } from '@aidkitorg/component-library';
import { useState } from 'react';
import { usePost } from './API';

export function PdfComponent() {
  const createPdf = usePost('/document/create_pdf');
  const verifyPdfCompatibility = usePost('/document/is_subsurvey_pdf_compatible');
  const [uid, setUid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subsurveyPath, setSubsurveyPath] = useState('');
  const [pdfCompatibility, setPdfCompatibility] = useState<{ compatible: boolean, unsupportedKinds: string[] } | null>(null);

  const downloadPdf = async () => {
    if (!uid || !subsurveyPath) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await createPdf({ uid, subsurveyPath, lang: 'en' });
      if (!response) {
        setError('No response from server');
        return;
      }

      if (response.error) {
        setError(response.error);
        return;
      }

      window.open(response.signedUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const verifySubsurveyPdfCompatibility = async () => {
    if (!subsurveyPath) return;

    setIsLoading(true);
    setError(null);
    setPdfCompatibility(null);

    try {
      const response = await verifyPdfCompatibility({ subsurveyPath });
      if (!response) {
        setError('No response from server');
        return;
      }
      if ('error' in response) {
        setError(response.error ?? 'Unknown error');
        return;
      }
      setPdfCompatibility(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify PDF compatibility');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full h-full p-4">
      <p className="mb-4 text-red-500">This feature is currently in beta and will not work for all applications at this time.</p>
      <p className="mb-4 text-red-500">Try verifying the subsurvey compatibility first using the button below and see the info note below.</p>
      <p className="mb-4">Enter a UID and subsurvey path to generate a PDF for the application form.</p>

      <div className="mt-4 text-sm text-gray-600">
        <Input label="UID" value={uid} onChange={(e) => setUid(e.target.value)} className="mb-4" />
        <Input label="Subsurvey Path" value={subsurveyPath} onChange={(e) => setSubsurveyPath(e.target.value)} className="mb-4" />
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded my-4">
          Error: {error}
        </div>
      )}

      <div className="my-4">
        <Button
          onClick={downloadPdf}
          isDisabled={isLoading || !uid || !subsurveyPath}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          {isLoading ? 'Generating PDF...' : 'Download PDF'}
        </Button>
      </div>

      <div className="my-4">
        <p>Verify the compatibility of the subsurvey with the PDF generator.</p>
        <Button
          onClick={verifySubsurveyPdfCompatibility}
          isDisabled={isLoading || !subsurveyPath}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          Verify PDF Compatibility
        </Button>
        <Card className="my-4 max-w-2xl">
          <CardHeader badgeVariant={'info'} badgeText={'Note on Compatibility'}></CardHeader>
          <CardBody>
            <Paragraph>
              This validity check does not include HTML elements which may be difficult to render correctly in a PDF.
            </Paragraph>
            <Paragraph>
              If there are unsupported component kinds, reach out to Riley for assistance.
            </Paragraph>
            <Paragraph>
              Issues with HTML styling can either be resolved in Distro or by contacting Riley if a solution can't be found.
            </Paragraph>
          </CardBody>
        </Card>
        {pdfCompatibility && (
          <Card className={`${pdfCompatibility.compatible ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-amber-100 border border-amber-400 text-amber-700'} px-4 py-3 rounded my-4`}>
            <CardHeader
              title={<h4 className={`${pdfCompatibility.compatible ? 'text-green-700' : 'text-amber-700'}`}>Subsurvey is {pdfCompatibility.compatible ? 'Compatible' : 'Incompatible'}</h4>}
            />
            {pdfCompatibility.compatible
              ? (
                <Paragraph>
                  Remember to verify that HTML styling looks correct in the PDF.
                </Paragraph>
              ) : (
                <Paragraph>
                  <strong>Unsupported Component Kinds</strong>
                  <ul>
                    {pdfCompatibility.unsupportedKinds.map((kind) => (
                      <li key={kind}>{kind}</li>
                    ))}
                  </ul>
                </Paragraph>
              )}
          </Card>
        )}
      </div>
    </div>
  );
}